import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CheckCircle, 
  Copy, 
  ArrowLeft, 
  Package, 
  Clock,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';
import { showUserError, showUserSuccess } from '@/utils/user-toast';
import { useMember } from '@/contexts/MemberContext';
import { extractFirstUrl } from '@/utils/url';


interface LocationState {
  topicName: string;
  memberType: string;
  downloadLink: string;
  remainingCycle: number;
  remainingToday: number;
}

const RedeemSuccessPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, updateQuota } = useMember();
  
  const state = location.state as LocationState | null;
  const isTotalMode = session?.member?.quota_mode === 'total';

  // 如果没有状态，返回首页
  useEffect(() => {
    if (!state) {
      navigate('/');
      return;
    }
    // 退出登录或会话失效后，浏览器后退不应重新展示成功页与资源内容
    if (!session) {
      navigate('/', { replace: true });
      return;
    }
    // 更新额度显示
    updateQuota(state.remainingCycle, state.remainingToday);
  }, [state, session, navigate, updateQuota]);

  // 复制内容
  const handleCopy = async () => {
    if (!state) return;
    try {
      await navigator.clipboard.writeText(state.downloadLink);
      showUserSuccess('内容已复制到剪贴板');
    } catch {
      showUserError('复制失败，请手动复制');
    }
  };

  // 打开链接（仅当内容中包含 HTTP/HTTPS 地址时使用）
  const handleOpen = () => {
    if (!state) return;
    const url = extractFirstUrl(state.downloadLink);
    if (!url) {
      showUserError('未识别到可跳转链接，请先复制完整内容');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const hasOpenableUrl = !!state && !!extractFirstUrl(state.downloadLink);

  // 渲染期守卫：退出登录或会话失效后，即使浏览器后退恢复路由状态，
  // 成功页与资源内容也不会被渲染（effect 中的跳转仅作为兜底）。
  if (!state || !session) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg">
        <CardContent className="p-8">
          {/* 成功图标 */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-green-800">领 取 成 功</h1>
            <p className="text-muted-foreground mt-1">
              {state.topicName}
            </p>
          </div>

          {/* 领取资源内容 */}
          <div className="bg-muted rounded-lg p-4 mb-6">
            <p className="text-sm text-muted-foreground mb-2">领取资源内容</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="min-w-0 flex-1 rounded border bg-white px-3 py-2 text-sm whitespace-pre-wrap break-words">
                {state.downloadLink}
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="outline" className="flex-1 sm:flex-none" onClick={handleCopy}>
                  <Copy className="h-4 w-4" />
                </Button>
                {hasOpenableUrl && (
                  <Button size="sm" className="flex-1 sm:flex-none" onClick={handleOpen}>
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* 版权声明 */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">使用声明</p>
                <p className="text-sm text-amber-700 mt-1">
                  内容仅供本人学习使用，请勿转发或传播。违规使用可能导致账号被封禁。
                </p>
              </div>
            </div>
          </div>

          {/* 额度信息 */}
          <div className={`mb-6 grid gap-4 ${isTotalMode ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div className="bg-white border rounded-lg p-4 text-center">
              <Package className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-xs text-muted-foreground">{isTotalMode ? '剩余可领' : '本周期剩余'}</p>
              <p className="text-xl font-bold">{state.remainingCycle}</p>
              <p className="text-xs text-muted-foreground">个</p>
            </div>
            {!isTotalMode && (
              <div className="bg-white border rounded-lg p-4 text-center">
                <Clock className="w-5 h-5 text-primary mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">今日剩余</p>
                <p className="text-xl font-bold">{state.remainingToday}</p>
                <p className="text-xs text-muted-foreground">次</p>
              </div>
            )}
          </div>

          {/* 返回按钮 */}
          <Button 
            onClick={() => navigate('/topics', { replace: true })}
            variant="outline" 
            className="w-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回主题列表
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default RedeemSuccessPage;
